import useTheme from "../../../../common/hooks/useTheme";
import { Box } from "@mui/material";
import React from "react";
import { Theme } from "../../../../providers/ThemeProvider";
import { ServiceContent } from "../../../new-reports/types/newReportTypes";
import fallBackImage from "../../../reports/images/coverPageImgLongevity.png";
import { enReportServiceType } from "../seviceBuilder/helper";

type Props = {
  service: ServiceContent;
  cardRef?: any;
  onClick?: () => void;
  showAttributes?: boolean;
  compact?: boolean;
};

const TreatmentPlanCard = ({
  service,
  cardRef,
  onClick,
  showAttributes = true,
  compact = false,
}: Props) => {
  const theme: Theme = useTheme();

  return (
    <Box
      sx={styles.cardContainer(theme, compact)}
      ref={cardRef}
      onClick={onClick}
    >
      {/* Image Container */}
      <Box sx={styles.imageContainer(compact)}>
        <Box
          component="img"
          src={service.imageURL || fallBackImage}
          alt={service.serviceName}
          sx={styles.image}
          onError={(e: any) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = fallBackImage;
          }}
        />
        {/* Service Type Badge (positioned absolutely) */}
        <Box sx={styles.badge()}>
          <Box component="p" sx={styles.badgeText(theme)}>
            {enReportServiceType[service.serviceTypeCode]}
          </Box>
        </Box>
      </Box>

      {/* Service Name */}
      <Box sx={styles.titleContainer}>
        <Box component="p" sx={styles.title(theme, compact)}>
          {service.serviceName}
        </Box>
      </Box>

      {/* Attributes Section */}
      {showAttributes &&
        service.attributes &&
        service.attributes.length > 0 && (
          <Box sx={styles.attributesContainer}>
            <Box sx={styles.attributesList}>
              {service.attributes.slice(0, 2).map((attribute, index) => (
                <Box key={index} sx={styles.attributeRow}>
                  <Box component="p" sx={styles.attributeLabel(theme)}>
                    {attribute.name}:{" "}
                  </Box>
                  <Box component="p" sx={styles.attributeValue(theme)}>
                    {attribute.value} {attribute.unit}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
    </Box>
  );
};

export default TreatmentPlanCard;

const styles = {
  cardContainer: (theme: Theme, compact?: boolean) => ({
    width: compact ? "100%" : "290px",
    minWidth: compact ? "auto" : "290px",
    maxWidth: compact ? "100%" : "290px",
    flexShrink: 0,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
    paddingBottom: "12px",
    position: "relative" as "relative",
    marginRight: compact ? "0" : "24px",
    boxSizing: "border-box" as "border-box",
    cursor: "pointer",
  }),
  imageContainer: (compact?: boolean) => ({
    width: "100%",
    height: compact ? "100px" : "126px",
    overflow: "hidden",
    position: "relative" as "relative",
    borderRadius: "8px",
  }),
  image: {
    width: "100%",
  },
  badge: () => ({
    position: "absolute" as "absolute",
    top: "10px",
    left: "11px",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    padding: "4px",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  badgeText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "20px",
    color: theme.colorTokens.P[90], // #04439C - Exact match
    margin: 0,
  }),
  titleContainer: {
    width: "100%",
    paddingLeft: "12px",
    paddingRight: "12px",
    display: "flex",
    flexDirection: "column" as "column",
  },
  title: (theme: Theme, compact?: boolean) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: compact ? "12px" : "16px",
    lineHeight: "normal",
    color: theme.colorTokens.N[90], // #3B3F42 - Exact match
    margin: 0,
    width: "100%",
    whiteSpace: "pre-wrap" as "pre-wrap",
  }),
  attributesContainer: {
    width: "100%",
    paddingLeft: "12px",
    paddingRight: "12px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  attributesList: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  attributeRow: {
    display: "flex",
    gap: "4px",
    alignItems: "center",
  },
  attributeLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colorTokens.N[80], // #6D7075 - Exact match
    margin: 0,
  }),
  attributeValue: (theme: Theme) => ({
    fontFamily: "var(--font-Light)",
    fontSize: "14px",
    color: theme.colorTokens.N[90], // #3B3F42 - Exact match
    margin: 0,
  }),
};
